<template>
  <div class="coupon-container">
    <div class="top-header-wrapper">
      <button @click="back">
        <img src="@/assets/icons/primary-back.svg" alt="back">
      </button>
      <p class="title">{{ pageTitle }}</p>
    </div>

    <div class="coupon-body">
      <section v-if="!isCartSelection" class="page-intro">
        <p class="page-intro-title">{{ t('myCoupon.intro.title') }}</p>
        <p class="page-intro-copy">{{ t('myCoupon.intro.description') }}</p>
      </section>

      <section v-if="!isCartSelection && !isNewMember" class="summary-card">
        <div class="summary-copy">
          <p class="summary-eyebrow">{{ t('myCoupon.summary.balanceLabel') }}</p>
          <p class="summary-value">{{ formattedCoinBalance }}</p>
          <p class="summary-subtitle">{{ t('myCoupon.summary.subtitle') }}</p>
        </div>
        <div class="summary-coin">
          <img src="@/assets/icons/m-coin.svg" alt="m-coin">
        </div>
      </section>

      <div v-if="isNewMember" class="promo-banner">
        <div class="promo-circle promo-circle--tr" />
        <div class="promo-circle promo-circle--bl" />
        <div class="promo-left">
          <div class="promo-coin-badge">
            <span class="promo-coin-inner">M</span>
          </div>
          <div class="promo-text">
            <p class="promo-eyebrow">สิทธิสมาชิกใหม่</p>
            <p class="promo-headline">ลงทะเบียนวันนี้ รับ 100 M-Coins ไปแลกคูปอง</p>
            <p class="promo-sub">ใช้ได้กับส่วนลดออนไลน์และคูปองหน้าร้านทุกสาขา</p>
          </div>
        </div>
        <button type="button" class="promo-cta" @click="goToRegister">
          <span class="promo-cta-label">ลงทะเบียนฟรี</span>
          <span class="promo-cta-arrow">
            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#2A38D6" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M5 12h13M12 5.5 18.5 12 12 18.5"/>
            </svg>
          </span>
          <span class="promo-cta-shimmer" />
        </button>
      </div>

      <div v-if="!isCartSelection && !isNewMember" class="legend-row">
        <div class="legend-item">
          <span class="legend-dot free" />
          <p>{{ t('myCoupon.legend.freeClaim') }}</p>
        </div>
        <div class="legend-item">
          <span class="legend-dot points" />
          <p>{{ t('myCoupon.legend.pointsClaim') }}</p>
        </div>
      </div>

      <div v-if="!isNewMember && coupons.length > 0" class="tab-wrapper">
        <button
          v-for="tab in tabs"
          :key="tab.value"
          class="tab-btn"
          :class="{ active: activeTab === tab.value }"
          @click="activeTab = tab.value"
        >
          {{ tab.label }}
        </button>
      </div>

      <div v-if="isNewMember" />

      <div v-else-if="showEmptyState" class="empty-state">
        <img src="@/assets/images/no-coupon.svg" alt="no-coupon">
        <p class="empty-title">{{ emptyTitle }}</p>
        <p class="empty-copy">{{ emptyCopy }}</p>
      </div>

      <div v-else-if="isCartSelection" class="coupon-panel">
        <div class="coupon-wrapper">
          <CouponCard
            v-for="coupon in filteredCoupons"
            :key="coupon.id"
            :coupon="couponCardProps(coupon)"
            mode="cart"
            @action="handleCouponAction"
            @detail="openDetailModal"
          />
          <div class="action-bar">
            <MButton :disabled="!selectedCoupon" class="action-btn" @click="goNext">{{ actionLabel }}</MButton>
          </div>
        </div>
      </div>

      <div v-else class="coupon-panel standalone">
        <div class="coupon-wrapper">
          <template v-if="isMyRewardsTab">
            <section class="coupon-section">
              <div class="section-heading">
                <div>
                  <p class="section-kicker">{{ t('myCoupon.rewards.availableKicker') }}</p>
                  <p class="section-title">{{ t('myCoupon.rewards.availableTitle') }}</p>
                </div>
                <span class="section-count">{{ actionableMyRewardsCoupons.length }}</span>
              </div>
              <div v-if="actionableMyRewardsCoupons.length > 0" class="coupon-list">
                <CouponCard
                  v-for="coupon in actionableMyRewardsCoupons"
                  :key="coupon.id"
                  :coupon="couponCardProps(coupon)"
                  mode="browse"
                  @action="handleCouponAction"
                  @detail="openDetailModal"
                />
              </div>
              <div v-else class="section-empty">
                {{ t('myCoupon.rewards.noAvailable') }}
              </div>
            </section>

            <section class="coupon-section faded">
              <div class="section-heading">
                <div>
                  <p class="section-kicker">{{ t('myCoupon.rewards.archivedKicker') }}</p>
                  <p class="section-title">{{ t('myCoupon.rewards.archivedTitle') }}</p>
                </div>
                <span class="section-count">{{ couponHistory.length }}</span>
              </div>
              <div v-if="couponHistory.length > 0" class="coupon-list">
                <CouponCard
                  v-for="coupon in couponHistory"
                  :key="coupon.history_key || coupon.id"
                  :coupon="couponCardProps(coupon)"
                  mode="browse"
                  @action="handleCouponAction"
                  @detail="openDetailModal"
                />
              </div>
              <div v-else class="section-empty">
                {{ t('myCoupon.rewards.noArchived') }}
              </div>
            </section>
          </template>

          <template v-else>
            <section v-if="usableCoupons.length > 0" class="coupon-section">
              <div class="section-heading">
                <div>
                  <p class="section-kicker">{{ t('myCoupon.sections.availableKicker') }}</p>
                  <p class="section-title">{{ t('myCoupon.sections.availableNow') }}</p>
                </div>
                <span class="section-count">{{ usableCoupons.length }}</span>
              </div>
              <div class="coupon-list">
                <CouponCard
                  v-for="coupon in usableCoupons"
                  :key="coupon.id"
                  :coupon="couponCardProps(coupon)"
                  mode="browse"
                  @action="handleCouponAction"
                  @detail="openDetailModal"
                />
              </div>
            </section>

            <section v-if="freeRedeemableCoupons.length > 0" class="coupon-section">
              <div class="section-heading">
                <div>
                  <p class="section-kicker">{{ t('myCoupon.sections.freeRedeemableKicker') }}</p>
                  <p class="section-title">{{ t('myCoupon.sections.freeRedeemable') }}</p>
                </div>
                <span class="section-count">{{ freeRedeemableCoupons.length }}</span>
              </div>
              <div class="coupon-list">
                <CouponCard
                  v-for="coupon in freeRedeemableCoupons"
                  :key="coupon.id"
                  :coupon="couponCardProps(coupon)"
                  mode="browse"
                  @action="handleCouponAction"
                  @detail="openDetailModal"
                />
              </div>
            </section>

            <section v-if="pointsRedeemableCoupons.length > 0" class="coupon-section">
              <div class="section-heading">
                <div>
                  <p class="section-kicker">{{ t('myCoupon.sections.pointsRedeemableKicker') }}</p>
                  <p class="section-title">{{ t('myCoupon.sections.pointsRedeemable') }}</p>
                </div>
                <span class="section-count">{{ pointsRedeemableCoupons.length }}</span>
              </div>
              <div class="coupon-list">
                <CouponCard
                  v-for="coupon in pointsRedeemableCoupons"
                  :key="coupon.id"
                  :coupon="couponCardProps(coupon)"
                  mode="browse"
                  @action="handleCouponAction"
                  @detail="openDetailModal"
                />
              </div>
            </section>

            <section v-if="lockedCoupons.length > 0" class="coupon-section faded">
              <div class="section-heading">
                <div>
                  <p class="section-kicker">{{ t('myCoupon.sections.lockedKicker') }}</p>
                  <p class="section-title">{{ t('myCoupon.sections.locked') }}</p>
                </div>
                <span class="section-count">{{ lockedCoupons.length }}</span>
              </div>
              <div class="coupon-list">
                <CouponCard
                  v-for="coupon in lockedCoupons"
                  :key="coupon.id"
                  :coupon="couponCardProps(coupon)"
                  mode="browse"
                  @action="handleCouponAction"
                  @detail="openDetailModal"
                />
              </div>
            </section>
          </template>
          <div class="action-bar">
            <MButton class="action-btn" @click="goNext">{{ actionLabel }}</MButton>
          </div>
        </div>
      </div>
    </div>

    <div v-if="qrCoupon" class="qr-modal-overlay" @click.self="closeQrModal">
      <div class="qr-modal">
        <button class="qr-close-btn" @click="closeQrModal">
          ×
        </button>
        <p class="qr-modal-title">{{ t('myCoupon.modal.scanTitle') }}</p>
        <p class="qr-modal-description">{{ t('myCoupon.modal.scanDescription') }}</p>
        <div class="qr-image-wrapper">
          <img :src="qrImageUrl" alt="clinic-benefit-qr">
        </div>
        <p class="qr-code-label">{{ t('myCoupon.modal.manualCode') }}</p>
        <p class="qr-code-value">{{ qrCoupon?.clinic_benefit_issue?.issued_code }}</p>
        <MButton class="qr-modal-btn" @click="closeQrModal">{{ t('ok') }}</MButton>
      </div>
    </div>

    <div v-if="detailCoupon" class="detail-modal-overlay" @click.self="closeDetailModal">
      <div class="detail-modal">
        <div class="detail-hero">
          <button class="detail-close-btn" @click="closeDetailModal">
            ×
          </button>
          <div class="detail-image-shell">
            <img :src="detailImageUrl" alt="coupon-detail-image">
          </div>
        </div>

        <div class="detail-content">
          <p class="detail-badge">{{ detailBadge }}</p>
          <h2 class="detail-title">{{ detailDisplayName }}</h2>
          <p v-if="detailDisplayCopy" class="detail-copy">{{ detailDisplayCopy }}</p>

          <div class="detail-section">
            <div class="detail-row">
              <div class="detail-icon">🕒</div>
              <div>
                <p class="detail-label">{{ t('myCoupon.detail.validityLabel') }}</p>
                <p class="detail-value">{{ detailValidity }}</p>
              </div>
            </div>

            <div v-if="detailCoupon.min_total > 0" class="detail-row">
              <div class="detail-icon">🛍</div>
              <div>
                <p class="detail-label">{{ t('myCoupon.detail.minimumLabel') }}</p>
                <p class="detail-value">{{ t('myCoupon.card.min') }} {{ detailCoupon.min_total }}฿</p>
              </div>
            </div>

            <div v-if="detailScopeLabel" class="detail-row">
              <div class="detail-icon">🏷</div>
              <div>
                <p class="detail-label">{{ t('myCoupon.detail.scopeLabel') }}</p>
                <p class="detail-value">{{ detailScopeLabel }}</p>
              </div>
            </div>

            <div v-if="detailCoupon.display_state === 'locked' && detailCoupon.lock_reason" class="detail-row">
              <div class="detail-icon">⚠</div>
              <div>
                <p class="detail-label">{{ t('myCoupon.detail.statusLabel') }}</p>
                <p class="detail-value">{{ detailCoupon.lock_reason }}</p>
              </div>
            </div>
          </div>

          <div class="detail-section">
            <p class="detail-terms-title">{{ t('myCoupon.detail.termsTitle') }}</p>
            <p class="detail-terms-copy">
              {{ detailTermsCopy }}
            </p>
          </div>

          <div class="detail-actions">
            <MButton
              class="detail-primary-btn"
              :success="isDetailFreeRedeemable"
              :blue="isDetailPointsRedeemable"
              :disabled="detailActionDisabled"
              @click="handleDetailPrimaryAction"
            >
              {{ detailActionLabel }}
            </MButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'

import CouponCard from '@/components/CouponCard'
import MButton from '@/components/Form/MButton'

export default {
  name: 'CouponView',
  components: {
    CouponCard,
    MButton,
  },
  setup() {
    const store = useStore()
    const route = useRoute()
    const router = useRouter()
    const { t } = useI18n()
    const activeTab = ref('all')
    const qrCoupon = ref(null)
    const detailCoupon = ref(null)
    let refreshTimer = null
    let handleVisibilityChange = null
    let handleWindowFocus = null

    const coupons = computed(() => store.state.cart.data?.coupon_promotions || [])
    const couponHistory = computed(() => store.state.cart.data?.coupon_history || [])
    const selectedCouponCode = computed(() => store.state.cart.data?.coupon_code || '')
    const isCartSelection = computed(() => route.name === 'Coupon')
    const cartReturnPath = computed(() => route.query.from || '/my-cart')
    const currentCoinBalance = computed(() => Number(store.state.cart.data?.coin || 0))
    const pageTitle = computed(() => (isCartSelection.value ? t('myCoupon.title') : t('myCoupon.myCouponsTitle')))
    const actionLabel = computed(() => (isCartSelection.value ? t('submit') : t('myCoupon.goToCart')))
    const formattedCoinBalance = computed(() => `${currentCoinBalance.value.toLocaleString()} M-Coins`)
    const tabs = computed(() => ([
      { value: 'all', label: t('myCoupon.tabs.all') },
      { value: 'birthday', label: t('myCoupon.tabs.birthday') },
      { value: 'points', label: t('myCoupon.tabs.points') },
      { value: 'in_store', label: t('myCoupon.tabs.inStore') },
      { value: 'mila_claire', label: t('myCoupon.tabs.milaClaire') },
      { value: 'makalin', label: t('myCoupon.tabs.makalin') },
      ...(!isCartSelection.value ? [{ value: 'my_rewards', label: t('myCoupon.tabs.myRewards') }] : [])
    ]))

    const filteredCoupons = computed(() => {
      if (activeTab.value === 'birthday') {
        return coupons.value.filter(coupon => coupon.is_birthday_month_only)
      }
      if (activeTab.value === 'points') {
        return coupons.value.filter(
          coupon => Number(coupon.burn_coin || 0) > 0 && ['redeemable', 'locked'].includes(coupon.display_state)
        )
      }
      if (activeTab.value === 'in_store') {
        return coupons.value.filter(coupon => coupon.benefit_type === 'clinic_benefit')
      }
      if (activeTab.value === 'mila_claire') {
        return coupons.value.filter(coupon => coupon.product_scope === 'mila_claire_only')
      }
      if (activeTab.value === 'makalin') {
        return coupons.value.filter(coupon => coupon.product_scope === 'exclude_mila_claire')
      }
      if (activeTab.value === 'my_rewards') {
        return coupons.value.filter(coupon => coupon.display_state === 'owned')
      }
      return coupons.value
    })

    const ownedCoupons = computed(() => filteredCoupons.value.filter(coupon => coupon.display_state === 'owned'))
    const ownedActiveCoupons = computed(() => ownedCoupons.value.filter((coupon) => {
      const effectiveExpired = coupon.owned_expires_at || coupon.expired
      return !effectiveExpired || dayjs(effectiveExpired).isAfter(dayjs())
    }))
    const actionableMyRewardsCoupons = computed(() => ownedActiveCoupons.value)
    const usableCoupons = computed(() => filteredCoupons.value.filter(coupon => ['owned', 'public'].includes(coupon.display_state)))
    const freeRedeemableCoupons = computed(() => filteredCoupons.value.filter(
      coupon => coupon.display_state === 'redeemable' && Number(coupon.burn_coin || 0) === 0
    ))
    const pointsRedeemableCoupons = computed(() => filteredCoupons.value.filter(
      coupon => coupon.display_state === 'redeemable' && Number(coupon.burn_coin || 0) > 0
    ))
    const lockedCoupons = computed(() => filteredCoupons.value.filter(coupon => coupon.display_state === 'locked'))
    const isMyRewardsTab = computed(() => activeTab.value === 'my_rewards')
    const isNewMember = computed(() => (
      !isCartSelection.value
      && currentCoinBalance.value === 0
      && coupons.value.length === 0
      && couponHistory.value.length === 0
    ))
    const showEmptyState = computed(() => {
      if (isCartSelection.value) {
        return filteredCoupons.value.length === 0
      }
      if (isMyRewardsTab.value) {
        return actionableMyRewardsCoupons.value.length === 0 && couponHistory.value.length === 0
      }
      return usableCoupons.value.length === 0
        && freeRedeemableCoupons.value.length === 0
        && pointsRedeemableCoupons.value.length === 0
        && lockedCoupons.value.length === 0
    })
    const emptyTitle = computed(() => (
      isMyRewardsTab.value
        ? t('myCoupon.rewards.emptyTitle')
        : t('myCoupon.noItem')
    ))
    const emptyCopy = computed(() => (
      isMyRewardsTab.value
        ? t('myCoupon.rewards.emptyCopy')
        : t('myCoupon.summary.subtitle')
    ))
    const qrImageUrl = computed(() => {
      const issuedCode = qrCoupon.value?.clinic_benefit_issue?.issued_code
      if (!issuedCode) {
        return ''
      }
      return `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(issuedCode)}`
    })
    const detailDisplayName = computed(() => {
      if (!detailCoupon.value) {
        return ''
      }
      if (detailCoupon.value.display_name) {
        return detailCoupon.value.display_name
      }
      return `${t('myCoupon.card.discount')} ${detailCoupon.value.discount_amount}${detailCoupon.value.discount_type === 'amount' ? '฿' : '%'}`
    })
    const detailImageUrl = computed(() => (
      detailCoupon.value?.picture_full || detailCoupon.value?.picture_url || detailCoupon.value?.picture_thumbnail || ''
    ))
    const detailDisplayCopy = computed(() => detailCoupon.value?.display_description || '')
    const detailValidity = computed(() => {
      if (!detailCoupon.value) {
        return ''
      }
      if (detailCoupon.value.display_state === 'history') {
        const historyAt = detailCoupon.value.history_at || detailCoupon.value.used_at || detailCoupon.value.expired_at
        return historyAt ? dayjs(historyAt).format('DD/MM/YYYY HH:mm') : t('myCoupon.card.noExpired')
      }
      const effectiveExpired = detailCoupon.value.owned_expires_at || detailCoupon.value.expired
      if (!effectiveExpired) {
        return t('myCoupon.card.noExpired')
      }
      return dayjs(effectiveExpired).format('DD/MM/YYYY')
    })
    const detailScopeLabel = computed(() => {
      if (!detailCoupon.value) {
        return ''
      }
      if (detailCoupon.value.is_birthday_month_only) {
        return t('myCoupon.card.birthdayOnly')
      }
      if (detailCoupon.value.product_scope === 'mila_claire_only') {
        return t('myCoupon.card.milaClaireOnly')
      }
      if (detailCoupon.value.product_scope === 'exclude_mila_claire') {
        return t('myCoupon.card.makalinOnly')
      }
      if (detailCoupon.value.benefit_type === 'clinic_benefit') {
        return t('myCoupon.card.clinicBenefit')
      }
      return ''
    })
    const detailBadge = computed(() => {
      if (!detailCoupon.value) {
        return ''
      }
      if (detailCoupon.value.display_state === 'history') {
        return detailCoupon.value.history_type === 'used'
          ? t('myCoupon.card.historyUsed')
          : t('myCoupon.card.historyExpired')
      }
      if (detailCoupon.value.benefit_type === 'clinic_benefit') {
        return t('myCoupon.card.badges.inStore')
      }
      if (detailCoupon.value.is_birthday_month_only) {
        return t('myCoupon.card.badges.birthday')
      }
      if (Number(detailCoupon.value.burn_coin || 0) > 0) {
        return t('myCoupon.card.badges.points')
      }
      return t('myCoupon.card.badges.coupon')
    })
    const detailTermsCopy = computed(() => {
      if (!detailCoupon.value) {
        return ''
      }
      if (detailCoupon.value.display_detail) {
        return detailCoupon.value.display_detail
      }
      return detailScopeLabel.value || t('myCoupon.detail.noTerms')
    })
    const detailActionLabel = computed(() => {
      if (!detailCoupon.value) {
        return ''
      }
      if (detailCoupon.value.display_state === 'history') {
        return detailCoupon.value.history_type === 'used'
          ? t('myCoupon.card.used')
          : t('myCoupon.card.expiredLabel')
      }
      if (detailCoupon.value.display_state === 'locked') {
        return t('myCoupon.card.locked')
      }
      if (detailCoupon.value.benefit_type === 'clinic_benefit' && detailCoupon.value.clinic_benefit_issue?.qr_token) {
        return t('myCoupon.card.showQr')
      }
      if (detailCoupon.value.display_state === 'redeemable') {
        return Number(detailCoupon.value.burn_coin || 0) > 0
          ? t('myCoupon.card.redeemWithPoints')
          : t('myCoupon.card.claimFree')
      }
      return t('myCoupon.card.useInCart')
    })
    const detailActionDisabled = computed(() => ['locked', 'history'].includes(detailCoupon.value?.display_state))
    const isDetailFreeRedeemable = computed(() => (
      detailCoupon.value?.display_state === 'redeemable' && Number(detailCoupon.value?.burn_coin || 0) === 0
    ))
    const isDetailPointsRedeemable = computed(() => (
      detailCoupon.value?.display_state === 'redeemable' && Number(detailCoupon.value?.burn_coin || 0) > 0
    ))

    const back = () => {
      if (isCartSelection.value) {
        router.push({ path: cartReturnPath.value })
        return
      }
      router.back()
    }
    const goToRegister = () => router.push('/user/register')
    const selectedCoupon = computed(() => filteredCoupons.value.find((coupon) => (
      selectedCouponCode.value === coupon.code
      && ['owned', 'public'].includes(coupon.display_state)
    )) || null)

    const refreshCouponList = async () => {
      if (isCartSelection.value) {
        await Promise.all([
          store.dispatch('cart/fetchGetCart'),
          store.dispatch('cart/fetchCouponList'),
        ])
        return
      }
      // Load the cart first so the M-Coin balance (store.state.cart.data.coin) is
      // populated on entry; then fetch the coupon lists (which set coupon_promotions/history).
      await store.dispatch('cart/fetchGetCart')
      await Promise.all([
        store.dispatch('cart/fetchRedeemableCouponList'),
        store.dispatch('cart/fetchCouponHistory'),
      ])
    }
    const closeQrModal = () => {
      qrCoupon.value = null
      refreshCouponList()
    }
    const openDetailModal = coupon => {
      detailCoupon.value = coupon
    }
    const closeDetailModal = () => {
      detailCoupon.value = null
    }
    const goNext = () => {
      if (isCartSelection.value) {
        router.push({ path: cartReturnPath.value })
        return
      }
      router.push({ path: '/my-cart' })
    }
    const couponCardProps = coupon => ({
      ...coupon,
      isSelected: selectedCoupon.value?.code === coupon.code,
    })
    const handleCouponAction = async (coupon) => {
      if (!coupon) {
        return
      }
      if (isCartSelection.value) {
        await store.dispatch('cart/fetchApplyCoupon', coupon.code)
        return
      }
      if (coupon.display_state === 'redeemable') {
        await store.dispatch('cart/fetchRedeemCoupon', coupon.code)
        return
      }
      if (coupon.benefit_type === 'clinic_benefit') {
        if (coupon.clinic_benefit_issue?.qr_token) {
          qrCoupon.value = coupon
        }
        return
      }
      if (['owned', 'public'].includes(coupon.display_state)) {
        router.push({ path: '/my-cart' })
      }
    }
    const handleDetailPrimaryAction = async () => {
      if (!detailCoupon.value || detailActionDisabled.value) {
        return
      }
      if (detailCoupon.value.benefit_type === 'clinic_benefit' && detailCoupon.value.clinic_benefit_issue?.qr_token) {
        qrCoupon.value = detailCoupon.value
        closeDetailModal()
        return
      }
      const coupon = detailCoupon.value
      await handleCouponAction(coupon)
      closeDetailModal()
    }

    onMounted(() => {
      refreshCouponList()

      if (!isCartSelection.value) {
        handleVisibilityChange = () => {
          if (document.visibilityState === 'visible') {
            refreshCouponList()
          }
        }
        handleWindowFocus = () => {
          refreshCouponList()
        }
        document.addEventListener('visibilitychange', handleVisibilityChange)
        window.addEventListener('focus', handleWindowFocus)
        refreshTimer = window.setInterval(() => {
          if (document.visibilityState === 'visible') {
            refreshCouponList()
          }
        }, 10000)
      }
    })

    onUnmounted(() => {
      if (handleVisibilityChange) {
        document.removeEventListener('visibilitychange', handleVisibilityChange)
      }
      if (handleWindowFocus) {
        window.removeEventListener('focus', handleWindowFocus)
      }
      if (refreshTimer) {
        window.clearInterval(refreshTimer)
      }
    })

    return {
      t,
      back,
      coupons,
      selectedCoupon,
      isCartSelection,
      pageTitle,
      actionLabel,
      activeTab,
      tabs,
      currentCoinBalance,
      formattedCoinBalance,
      filteredCoupons,
      usableCoupons,
      freeRedeemableCoupons,
      pointsRedeemableCoupons,
      lockedCoupons,
      actionableMyRewardsCoupons,
      ownedActiveCoupons,
      couponHistory,
      isMyRewardsTab,
      showEmptyState,
      emptyTitle,
      emptyCopy,
      qrCoupon,
      qrImageUrl,
      detailCoupon,
      detailImageUrl,
      detailDisplayName,
      detailDisplayCopy,
      detailValidity,
      detailScopeLabel,
      detailBadge,
      detailTermsCopy,
      detailActionLabel,
      detailActionDisabled,
      isDetailFreeRedeemable,
      isDetailPointsRedeemable,
      closeQrModal,
      openDetailModal,
      closeDetailModal,
      goNext,
      couponCardProps,
      handleCouponAction,
      handleDetailPrimaryAction,
      isNewMember,
      goToRegister,
    }
  }
}
</script>

<style lang="scss" scoped>
.coupon-container {
  background: linear-gradient(180deg, #f8f9ff 0%, #eef2ff 100%);
}

.top-header-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px 16px;
  border-bottom: 1px solid rgba(214, 226, 239, 0.9);
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(14px);
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  z-index: 20;
  @media screen and (min-width: 780px) {
    left: 50%;
    right: auto;
    transform: translateX(-50%);
    width: 780px;
  }
  > button {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    outline: none;
    background-color: transparent;
    border: 0;
    padding: 8px;
  }
}

.title {
  font-size: 30px;
  line-height: 1.1;
  font-weight: 800;
  letter-spacing: -0.03em;
  color: #10182c;
}

.coupon-body {
  padding-top: 76px;
  @media screen and (min-width: 1024px) {
    width: min(1180px, calc(100vw - 64px));
    margin: 0 auto;
    padding-top: 112px;
    padding-bottom: 24px;
  }
}

.page-intro {
  margin: 18px 16px 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  @media screen and (min-width: 1024px) {
    margin: 0;
    gap: 8px;
  }
}

.page-intro-title {
  font-size: 28px;
  line-height: 1.08;
  font-weight: 800;
  letter-spacing: -0.03em;
  color: #10182c;
  @media screen and (min-width: 1024px) {
    font-size: 40px;
  }
}

.page-intro-copy {
  font-size: 14px;
  line-height: 1.6;
  color: #6c7890;
  max-width: 720px;
  @media screen and (min-width: 1024px) {
    font-size: 16px;
  }
}

.summary-card {
  margin: 18px 16px 0;
  padding: 18px 18px 16px;
  border-radius: 24px;
  background: linear-gradient(135deg, #1c2dec 0%, #6d7cff 100%);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  box-shadow: 0 20px 40px rgba(28, 45, 236, 0.18);
  @media screen and (min-width: 1024px) {
    margin: 8px 0 0;
    padding: 28px 30px;
    border-radius: 30px;
    min-height: 176px;
  }
}

.summary-copy {
  min-width: 0;
}

.summary-eyebrow {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  opacity: 0.82;
}

.summary-value {
  margin-top: 6px;
  font-size: 28px;
  font-weight: 800;
  letter-spacing: -0.03em;
  @media screen and (min-width: 1024px) {
    margin-top: 10px;
    font-size: 40px;
  }
}

.summary-subtitle {
  margin-top: 6px;
  font-size: 13px;
  line-height: 1.5;
  opacity: 0.84;
  @media screen and (min-width: 1024px) {
    max-width: 560px;
    font-size: 15px;
  }
}

.summary-coin {
  width: 64px;
  height: 64px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  > img {
    width: 34px;
    height: 34px;
  }
  @media screen and (min-width: 1024px) {
    width: 92px;
    height: 92px;
    border-radius: 28px;
    > img {
      width: 48px;
      height: 48px;
    }
  }
}

.legend-row {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin: 14px 16px 0;
  @media screen and (min-width: 1024px) {
    margin: 18px 0 0;
    gap: 22px;
  }
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  p {
    font-size: 11px;
    font-weight: 700;
    color: #69778f;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  box-shadow: 0 0 0 4px rgba(0, 0, 0, 0.04);
  &.free {
    background: #4cc764;
  }
  &.points {
    background: #1c2dec;
  }
}

.tab-wrapper {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding: 18px 16px 0;
  @media screen and (min-width: 1024px) {
    gap: 18px;
    padding: 22px 0 0;
    margin-bottom: 4px;
    border-bottom: 1px solid rgba(214, 226, 239, 0.75);
  }
}

.tab-btn {
  border: 1px solid #d9e0f1;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 999px;
  padding: 9px 16px;
  white-space: nowrap;
  color: #6e7b90;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
  &.active {
    background-color: var(--secondary-color);
    border-color: var(--secondary-color);
    color: var(--white-color);
    box-shadow: 0 12px 24px rgba(28, 45, 236, 0.18);
  }
  @media screen and (min-width: 1024px) {
    background: transparent;
    border: 0;
    border-radius: 0;
    padding: 0 0 14px;
    box-shadow: none;
    font-size: 15px;
    font-weight: 700;
    color: #73819a;
    position: relative;
    &.active {
      background: transparent;
      color: #1c2dec;
      box-shadow: none;
      &::after {
        content: "";
        position: absolute;
        left: 0;
        right: 0;
        bottom: -1px;
        height: 3px;
        border-radius: 999px;
        background: #1c2dec;
      }
    }
  }
}

.coupon-panel {
  display: block;
}

.coupon-wrapper {
  display: flex;
  flex-direction: column;
  padding: 18px 16px 116px;
  gap: 22px;
  @media screen and (min-width: 1024px) {
    padding: 24px 0 132px;
    gap: 28px;
  }
}

.coupon-section {
  display: flex;
  flex-direction: column;
  gap: 14px;
  &.faded {
    opacity: 0.78;
  }
  @media screen and (min-width: 1024px) {
    gap: 18px;
  }
}

.section-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
}

.section-kicker {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #7d88a0;
}

.section-title {
  margin-top: 4px;
  font-size: 22px;
  line-height: 1.15;
  font-weight: 800;
  color: #16233a;
  @media screen and (min-width: 1024px) {
    font-size: 28px;
  }
}

.section-count {
  min-width: 32px;
  height: 32px;
  padding: 0 10px;
  border-radius: 999px;
  background: rgba(28, 45, 236, 0.08);
  color: #1c2dec;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 800;
}

.coupon-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  @media screen and (min-width: 1024px) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 18px;
  }
}

.section-empty,
.empty-state {
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.76);
  border: 1px dashed #d6deef;
}

.section-empty {
  padding: 22px 18px;
  font-size: 14px;
  color: #7a879b;
}

.empty-state {
  margin: 24px 16px 0;
  padding: 36px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  > img {
    width: 120px;
    height: auto;
  }
  @media screen and (min-width: 1024px) {
    margin: 28px 0 0;
    min-height: 320px;
    justify-content: center;
  }
}

.empty-title {
  margin-top: 18px;
  font-size: 18px;
  font-weight: 800;
  color: #172540;
}

.empty-copy {
  margin-top: 8px;
  font-size: 14px;
  line-height: 1.5;
  color: #748297;
  max-width: 280px;
}

.action-bar {
  flex-shrink: 0;
  padding: 14px 16px 18px;
  margin-top: 0;
  background: transparent;
  @media screen and (min-width: 780px) {
    padding: 14px 40px 18px;
  }
  @media screen and (min-width: 1024px) {
    padding: 18px 0 8px;
  }
}

.action-btn {
  width: 100%;
  @media screen and (min-width: 1024px) {
    width: auto;
    min-width: 260px;
    margin-left: auto;
    display: block;
  }
}

.qr-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(6px);
}

.qr-modal {
  position: relative;
  width: min(100%, 360px);
  border-radius: 24px;
  background: linear-gradient(180deg, #ffffff 0%, #f7f9ff 100%);
  padding: 28px 24px 24px;
  box-shadow: 0 28px 80px rgba(25, 39, 72, 0.24);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  @media screen and (min-width: 1024px) {
    width: min(100%, 420px);
    border-radius: 28px;
    padding: 34px 32px 28px;
  }
}

.qr-close-btn {
  position: absolute;
  top: 12px;
  right: 14px;
  border: 0;
  background: transparent;
  font-size: 28px;
  line-height: 1;
  color: #7a8798;
  cursor: pointer;
}

.qr-modal-title {
  font-size: 24px;
  font-weight: 800;
  color: #16233a;
}

.qr-modal-description {
  margin-top: 8px;
  font-size: 14px;
  line-height: 1.5;
  color: #66758a;
}

.qr-image-wrapper {
  width: 240px;
  height: 240px;
  margin-top: 20px;
  padding: 12px;
  border-radius: 20px;
  background: #ffffff;
  box-shadow: inset 0 0 0 1px #e6ebf4;
}

.qr-image-wrapper > img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.qr-code-label {
  margin-top: 18px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #7a8798;
}

.qr-code-value {
  margin-top: 8px;
  font-size: 24px;
  font-weight: 800;
  letter-spacing: 0.12em;
  color: #1f2f4a;
  word-break: break-word;
}

.qr-modal-btn {
  width: 100%;
  margin-top: 20px;
}

.detail-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(0, 0, 0, 0.62);
  backdrop-filter: blur(10px);
}

.detail-modal {
  width: min(100%, 440px);
  max-height: min(92vh, 860px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-radius: 32px;
  background: #ffffff;
  box-shadow: 0 30px 80px rgba(12, 22, 48, 0.28);
}

.detail-hero {
  position: relative;
  height: 196px;
  background: linear-gradient(135deg, #9097e3 0%, #1c2dec 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.detail-close-btn {
  position: absolute;
  top: 18px;
  right: 18px;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 0;
  background: rgba(0, 0, 0, 0.18);
  color: #ffffff;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
}

.detail-image-shell {
  width: 132px;
  height: 132px;
  padding: 10px;
  box-sizing: border-box;
  border-radius: 28px;
  background: #ffffff;
  overflow: hidden;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.18);
  > img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center top;
  }
}

.detail-content {
  padding: 28px 24px 24px;
  overflow-y: auto;
}

.detail-badge {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  background: rgba(28, 45, 236, 0.1);
  color: #1c2dec;
  padding: 6px 11px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.detail-title {
  margin-top: 16px;
  font-size: 30px;
  line-height: 1.14;
  font-weight: 800;
  color: #111a2d;
}

.detail-copy {
  margin-top: 10px;
  color: #68768c;
  font-size: 15px;
  line-height: 1.65;
}

.detail-section {
  margin-top: 28px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.detail-row {
  display: flex;
  gap: 14px;
  align-items: flex-start;
}

.detail-icon {
  width: 40px;
  height: 40px;
  border-radius: 14px;
  background: #eef2ff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}

.detail-label {
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #9aa5b5;
}

.detail-value {
  margin-top: 4px;
  font-size: 15px;
  line-height: 1.5;
  font-weight: 700;
  color: #1c2941;
}

.detail-terms-title {
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #9aa5b5;
}

.detail-terms-copy {
  margin-top: 8px;
  font-size: 14px;
  line-height: 1.75;
  color: #5f6d84;
  white-space: pre-line;
}

.detail-actions {
  margin-top: 32px;
}

.detail-primary-btn {
  width: 100%;
}

/* ── Promo banner (new member empty state) ── */
@keyframes bob {
  0%, 100% { transform: translateY(0); }
  50%       { transform: translateY(-6px); }
}
@keyframes nudge {
  0%, 100% { transform: translateX(0); }
  60%       { transform: translateX(5px); }
}
@keyframes shimmer {
  0%   { transform: translateX(-120%); }
  100% { transform: translateX(340%); }
}

.promo-banner {
  position: relative;
  overflow: hidden;
  margin: 18px 16px 0;
  padding: 36px 40px;
  border-radius: 20px;
  background: linear-gradient(115deg, #3b4bf4 0%, #5361fa 48%, #7c6bff 100%);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 32px;

  @media screen and (min-width: 1024px) {
    margin: 18px 0 0;
    border-radius: 24px;
  }
}

.promo-circle {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;

  &--tr {
    right: -40px;
    top: -60px;
    width: 260px;
    height: 260px;
    background: rgba(255, 255, 255, 0.08);
  }

  &--bl {
    right: 70px;
    bottom: -90px;
    width: 180px;
    height: 180px;
    background: rgba(255, 255, 255, 0.06);
  }
}

.promo-left {
  position: relative;
  display: flex;
  align-items: center;
  gap: 24px;
  flex: 1;
  min-width: 0;
}

.promo-coin-badge {
  flex-shrink: 0;
  width: 72px;
  height: 72px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: bob 4.6s ease-in-out infinite;
}

.promo-coin-inner {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(140deg, #f8d778, #e0a21b);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'IBM Plex Sans Thai', system-ui, sans-serif;
  font-weight: 700;
  font-size: 22px;
  color: #5a3d00;
}

.promo-text {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.promo-eyebrow {
  font-family: 'IBM Plex Sans Thai', system-ui, sans-serif;
  font-weight: 600;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.78);
  letter-spacing: 0.06em;
}

.promo-headline {
  font-family: 'IBM Plex Sans Thai', system-ui, sans-serif;
  font-weight: 700;
  font-size: 24px;
  line-height: 1.25;
  color: #ffffff;

  @media screen and (min-width: 1024px) {
    font-size: 28px;
  }
}

.promo-sub {
  font-family: 'IBM Plex Sans Thai', system-ui, sans-serif;
  font-weight: 400;
  font-size: 15px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.82);
}

.promo-cta {
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 19px 34px;
  border: none;
  border-radius: 999px;
  background: #ffffff;
  color: #2a38d6;
  font-family: 'IBM Plex Sans Thai', system-ui, sans-serif;
  font-weight: 700;
  font-size: 19px;
  cursor: pointer;
  box-shadow: 0 12px 26px rgba(12, 20, 80, 0.28);
  transition: transform 0.16s, box-shadow 0.16s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 36px rgba(12, 20, 80, 0.34);
  }

  &:active {
    transform: translateY(0);
  }
}

.promo-cta-label {
  position: relative;
  z-index: 1;
}

.promo-cta-arrow {
  position: relative;
  z-index: 1;
  display: flex;
  animation: nudge 1.9s ease-in-out infinite;
}

.promo-cta-shimmer {
  position: absolute;
  inset: 0;
  width: 34%;
  background: linear-gradient(90deg, transparent, rgba(59, 75, 244, 0.18), transparent);
  animation: shimmer 4s ease-in-out infinite;
}

@media screen and (max-width: 640px) {
  .promo-banner {
    flex-direction: column;
    align-items: flex-start;
    padding: 28px 24px;
    gap: 20px;
  }

  .promo-headline {
    font-size: 20px;
  }

  .promo-cta {
    padding: 15px 28px;
    font-size: 16px;
  }
}
</style>
